import { useState, useCallback, useRef } from 'react';
import { Wifi, Loader2, CheckCircle2, AlertCircle, RefreshCw, ArrowUp, ArrowDown, Clock } from 'lucide-react';
import WiFiSpeedGauge, { getSpeedTier } from './WiFiSpeedGauge';

/**
 * WiFi Speed Test Component using M-Lab NDT7
 * Same infrastructure as Google's speed test for accurate measurements
 */
export default function WiFiSpeedTest({ onSpeedResult, className = '' }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const workerRef = useRef(null);

  // Detect device type
  const detectDeviceType = () => {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  };

  // Get time of day category
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  };

  // Run M-Lab NDT7 speed test
  const runSpeedTest = useCallback(async () => {
    setTesting(true);
    setError(null);
    setProgress(0);
    setResult(null);
    setCurrentSpeed(0);
    setStatusText('Finding nearest M-Lab server...');

    try {
      // Step 1: Get nearest M-Lab server using Locate API
      setProgress(5);
      const locateResponse = await fetch('https://locate.measurementlab.net/v2/nearest/ndt/ndt7?client_name=strider');
      
      if (!locateResponse.ok) {
        throw new Error('Could not find M-Lab server');
      }
      
      const locateData = await locateResponse.json();
      const serverUrls = locateData?.results?.[0]?.urls;
      
      if (!serverUrls) {
        throw new Error('No M-Lab servers available');
      }

      // Get WebSocket URLs for download and upload
      const wsDownloadUrl = serverUrls['wss:///ndt/v7/download'];
      const wsUploadUrl = serverUrls['wss:///ndt/v7/upload'];

      if (!wsDownloadUrl || !wsUploadUrl) {
        throw new Error('M-Lab server URLs not available');
      }

      setProgress(10);
      setStatusText('Testing download speed...');

      // Step 2: Run download test
      const downloadResult = await runNDT7Download(wsDownloadUrl, (speed, prog) => {
        setCurrentSpeed(speed);
        setProgress(10 + prog * 40);
      });

      setProgress(55);
      setStatusText('Testing upload speed...');
      setCurrentSpeed(0);

      // Step 3: Run upload test
      const uploadResult = await runNDT7Upload(wsUploadUrl, (speed, prog) => {
        setCurrentSpeed(speed);
        setProgress(55 + prog * 35);
      });

      setProgress(95);
      setStatusText('Calculating results...');

      // Calculate final results
      const downloadMbps = downloadResult.speed;
      const uploadMbps = uploadResult.speed;
      const latencyMs = Math.min(downloadResult.minRTT || 50, uploadResult.minRTT || 50);
      const tier = getSpeedTier(downloadMbps);

      const finalResult = {
        download_mbps: Math.round(downloadMbps * 10) / 10,
        upload_mbps: Math.round(uploadMbps * 10) / 10,
        latency_ms: Math.round(latencyMs),
        jitter_ms: Math.round(Math.abs((downloadResult.minRTT || 50) - (uploadResult.minRTT || 50))),
        speed_tier: tier,
        tested_at: new Date().toISOString(),
        test_method: 'mlab-ndt7',
        device_type: detectDeviceType(),
        time_of_day: getTimeOfDay()
      };

      setProgress(100);
      setStatusText('Complete!');
      setResult(finalResult);
      setCurrentSpeed(0);

      if (onSpeedResult) {
        onSpeedResult(finalResult);
      }

    } catch (err) {
      console.error('M-Lab speed test error:', err);
      // Fallback to simple test if M-Lab fails
      try {
        setStatusText('Falling back to alternative test...');
        const fallbackResult = await runFallbackSpeedTest((prog, status) => {
          setProgress(prog);
          if (status) setStatusText(status);
        });
        setResult(fallbackResult);
        if (onSpeedResult) {
          onSpeedResult(fallbackResult);
        }
      } catch (fallbackErr) {
        setError('Speed test failed. Please check your connection and try again.');
      }
    } finally {
      setTesting(false);
    }
  }, [onSpeedResult]);

  // NDT7 Download Test
  const runNDT7Download = (wsUrl, onProgress) => {
    return new Promise((resolve, reject) => {
      const startTime = performance.now();
      let totalBytes = 0;
      let lastSpeed = 0;
      let minRTT = Infinity;
      const timeout = setTimeout(() => {
        ws.close();
        resolve({ speed: lastSpeed, minRTT: minRTT === Infinity ? 50 : minRTT });
      }, 12000); // 12 second max test duration

      const ws = new WebSocket(wsUrl, 'net.measurementlab.ndt.v7');
      
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        // Test started
      };

      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          totalBytes += event.data.byteLength;
          const elapsed = (performance.now() - startTime) / 1000;
          const speedMbps = (totalBytes * 8) / (elapsed * 1000000);
          lastSpeed = speedMbps;
          const progress = Math.min(elapsed / 10, 1); // 10 second test
          onProgress(speedMbps, progress);
        } else {
          // JSON measurement message
          try {
            const measurement = JSON.parse(event.data);
            if (measurement.TCPInfo?.MinRTT) {
              minRTT = Math.min(minRTT, measurement.TCPInfo.MinRTT / 1000); // Convert to ms
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      };

      ws.onerror = (err) => {
        clearTimeout(timeout);
        reject(err);
      };

      ws.onclose = () => {
        clearTimeout(timeout);
        const elapsed = (performance.now() - startTime) / 1000;
        const finalSpeed = (totalBytes * 8) / (elapsed * 1000000);
        resolve({ speed: finalSpeed || lastSpeed, minRTT: minRTT === Infinity ? 50 : minRTT });
      };
    });
  };

  // NDT7 Upload Test
  const runNDT7Upload = (wsUrl, onProgress) => {
    return new Promise((resolve, reject) => {
      const startTime = performance.now();
      let totalBytes = 0;
      let lastSpeed = 0;
      let minRTT = Infinity;
      const chunkSize = 1 << 13; // 8KB chunks
      const chunk = new Uint8Array(chunkSize);
      
      const timeout = setTimeout(() => {
        ws.close();
        resolve({ speed: lastSpeed, minRTT: minRTT === Infinity ? 50 : minRTT });
      }, 12000);

      const ws = new WebSocket(wsUrl, 'net.measurementlab.ndt.v7');
      ws.binaryType = 'arraybuffer';

      const sendData = () => {
        const elapsed = (performance.now() - startTime) / 1000;
        if (elapsed >= 10 || ws.readyState !== WebSocket.OPEN) {
          ws.close();
          return;
        }

        // Send data while buffer is not too full
        while (ws.bufferedAmount < 1 << 20 && ws.readyState === WebSocket.OPEN) {
          ws.send(chunk);
          totalBytes += chunkSize;
        }

        const speedMbps = (totalBytes * 8) / (elapsed * 1000000);
        lastSpeed = speedMbps;
        const progress = Math.min(elapsed / 10, 1);
        onProgress(speedMbps, progress);

        if (ws.readyState === WebSocket.OPEN) {
          setTimeout(sendData, 10);
        }
      };

      ws.onopen = () => {
        sendData();
      };

      ws.onmessage = (event) => {
        if (typeof event.data === 'string') {
          try {
            const measurement = JSON.parse(event.data);
            if (measurement.TCPInfo?.MinRTT) {
              minRTT = Math.min(minRTT, measurement.TCPInfo.MinRTT / 1000);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      };

      ws.onerror = (err) => {
        clearTimeout(timeout);
        reject(err);
      };

      ws.onclose = () => {
        clearTimeout(timeout);
        const elapsed = (performance.now() - startTime) / 1000;
        const finalSpeed = (totalBytes * 8) / (elapsed * 1000000);
        resolve({ speed: finalSpeed || lastSpeed, minRTT: minRTT === Infinity ? 50 : minRTT });
      };
    });
  };

  // Fallback speed test using Cloudflare
  const runFallbackSpeedTest = async (onProgress) => {
    onProgress(10, 'Using alternative test...');
    
    // Download test using Cloudflare
    const downloadSpeeds = [];
    const testUrls = [
      'https://speed.cloudflare.com/__down?bytes=10000000',
      'https://speed.cloudflare.com/__down?bytes=25000000',
    ];

    for (let i = 0; i < testUrls.length; i++) {
      const startTime = performance.now();
      const response = await fetch(testUrls[i], { cache: 'no-store' });
      const data = await response.arrayBuffer();
      const elapsed = (performance.now() - startTime) / 1000;
      const speedMbps = (data.byteLength * 8) / (elapsed * 1000000);
      downloadSpeeds.push(speedMbps);
      onProgress(30 + i * 25, `Download test ${i + 1}/${testUrls.length}...`);
    }

    const avgDownload = downloadSpeeds.reduce((a, b) => a + b, 0) / downloadSpeeds.length;
    
    // Upload estimation (typically 10-20% of download for most connections)
    onProgress(80, 'Estimating upload speed...');
    const uploadEstimate = avgDownload * 0.15;

    // Latency test
    onProgress(90, 'Measuring latency...');
    const latencies = [];
    for (let i = 0; i < 5; i++) {
      const start = performance.now();
      await fetch('https://speed.cloudflare.com/__down?bytes=1', { cache: 'no-store' });
      latencies.push(performance.now() - start);
    }
    const avgLatency = latencies.reduce((a, b) => a + b, 0) / latencies.length;

    const tier = getSpeedTier(avgDownload);

    return {
      download_mbps: Math.round(avgDownload * 10) / 10,
      upload_mbps: Math.round(uploadEstimate * 10) / 10,
      latency_ms: Math.round(avgLatency),
      jitter_ms: Math.round(Math.max(...latencies) - Math.min(...latencies)),
      speed_tier: tier,
      tested_at: new Date().toISOString(),
      test_method: 'cloudflare-fallback',
      device_type: detectDeviceType(),
      time_of_day: getTimeOfDay()
    };
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-teal-50 rounded-lg">
            <Wifi className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">WiFi Speed Test</h3>
            <p className="text-xs text-gray-500">Test the venue's internet speed</p>
          </div>
        </div>
        
        {result && !testing && (
          <button
            onClick={runSpeedTest}
            className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
            title="Test again"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Speed Gauge */}
      <div className="flex justify-center mb-4">
        <WiFiSpeedGauge 
          speed={testing ? currentSpeed : (result?.download_mbps || 0)} 
          size="md"
          showTier={!testing}
          animated={testing}
        />
      </div>

      {/* Status/Progress */}
      {testing && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">{statusText}</span>
            <span className="text-teal-600 font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          {currentSpeed > 0 && (
            <p className="text-center text-sm text-gray-500 mt-2">
              Current: <span className="font-medium text-gray-700">{currentSpeed.toFixed(1)} Mbps</span>
            </p>
          )}
        </div>
      )}

      {/* Results */}
      {result && !testing && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex items-center justify-center gap-1 text-teal-600 mb-1">
                <ArrowDown className="w-3 h-3" />
                <span className="text-lg font-bold">{result.download_mbps}</span>
              </div>
              <p className="text-xs text-gray-500">Download</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                <ArrowUp className="w-3 h-3" />
                <span className="text-lg font-bold">{result.upload_mbps}</span>
              </div>
              <p className="text-xs text-gray-500">Upload</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                <Clock className="w-3 h-3" />
                <span className="text-lg font-bold">{result.latency_ms}ms</span>
              </div>
              <p className="text-xs text-gray-500">Latency</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg p-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>Speed recorded! This will be shown on the venue listing.</span>
          </div>

          <p className="text-xs text-gray-400 text-center">
            {result.test_method === 'mlab-ndt7' ? 'Powered by M-Lab (same as Google)' : 'Measured via Cloudflare'}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Start Test Button */}
      {!testing && !result && (
        <button
          onClick={runSpeedTest}
          className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          data-testid="start-speed-test"
        >
          <Wifi className="w-4 h-4" />
          Start Speed Test
        </button>
      )}

      {/* Testing indicator */}
      {testing && (
        <div className="flex items-center justify-center gap-2 text-teal-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">Testing in progress...</span>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center mt-3">
        For accurate results, run this test while connected to the venue's WiFi
      </p>
    </div>
  );
}
